import axios, { type AxiosError } from 'axios'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080'
const REQUEST_TIMEOUT = 15_000
const MAX_RETRIES = 2
const RETRY_DELAY = 1000

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: REQUEST_TIMEOUT,
  headers: { 'Content-Type': 'application/json' },
})

function authHeaders(userId: string) {
  return { Authorization: `Bearer ${userId}` }
}

function sanitizeEmail(email: string): string {
  return email.trim().toLowerCase().replace(/[<>'";&]/g, '')
}

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

async function withRetry<T>(fn: () => Promise<T>, retries = MAX_RETRIES): Promise<T> {
  for (let i = 0; i <= retries; i++) {
    try {
      return await fn()
    } catch (err) {
      const axErr = err as AxiosError
      const status = axErr?.response?.status
      if (status && status >= 400 && status < 500) throw err
      if (i === retries) throw err
      await new Promise((r) => setTimeout(r, RETRY_DELAY * (i + 1)))
    }
  }
  throw new Error('Max retries exceeded')
}

const rateLimits = new Map<string, number>()

function checkRateLimit(key: string, cooldownMs: number): boolean {
  const last = rateLimits.get(key) || 0
  if (Date.now() - last < cooldownMs) return false
  rateLimits.set(key, Date.now())
  return true
}

export function sanitizeErrorMessage(err: unknown): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const axErr = err as { response?: { status?: number; data?: { error?: string } } }
    if (axErr.response?.status === 404) {
      return 'No account found with that email. Make sure you use your Perceptrons dashboard email.'
    }
    if (axErr.response?.status === 429) {
      return 'Too many requests. Please wait a moment.'
    }
    const msg = axErr.response?.data?.error
    if (msg && typeof msg === 'string' && msg.length < 200) {
      return msg.replace(/<[^>]*>/g, '')
    }
    return 'Request failed. Please try again.'
  }
  if (err instanceof Error && err.message === 'Network Error') {
    return 'Could not connect to server. Check your connection.'
  }
  return 'An unexpected error occurred. Please try again.'
}

export const login = async (email: string) => {
  const clean = sanitizeEmail(email)
  if (!isValidEmail(clean)) throw new Error('Invalid email format')
  if (!checkRateLimit('login', 2000)) throw new Error('Please wait before trying again')

  const response = await withRetry(() =>
    api.post('/api/auth/login', { email: clean })
  )
  return response.data as { user_id: string; email: string; balance: number }
}

export const spinWheel = async (userId: string, pointsCost: number = 1000) => {
  if (pointsCost < 0 || !Number.isInteger(pointsCost)) throw new Error('Invalid spin cost')
  if (!checkRateLimit('spin', 500)) throw new Error('Spin cooldown active')

  const response = await api.post('/api/spin', { points_cost: pointsCost }, {
    headers: authHeaders(userId),
  })
  return response.data
}

export const getBalance = async (userId: string) => {
  const response = await withRetry(() =>
    api.get('/api/balance', { headers: authHeaders(userId) })
  )
  return response.data as { balance: number }
}

export const getSpins = async (userId: string, limit: number = 10) => {
  const response = await api.get(`/api/spins?limit=${Math.min(limit, 50)}`, {
    headers: authHeaders(userId),
  })
  return response.data
}

export const getAuditEvents = async (userId: string, limit: number = 10) => {
  const response = await api.get(`/api/audit/events?limit=${Math.min(limit, 50)}`, {
    headers: authHeaders(userId),
  })
  return response.data
}

export interface EligibilityData {
  user_id: string
  total_spins: number
  total_points_burned: number
  spins_since_rare_win: number
  spins_since_ultra_win: number
  eligible_tiers: string[]
  next_tier_unlock: {
    rare: { spins_needed: number; points_needed: number }
    ultra: { spins_needed: number; points_needed: number }
  }
}

export const getEligibility = async (userId: string): Promise<EligibilityData> => {
  const response = await api.get('/api/eligibility', {
    headers: authHeaders(userId),
  })
  return response.data
}
